import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "./ui/button";
import { Trash2 } from "lucide-react";
import { generateClient } from "aws-amplify/api";
import {
  deleteEvent,
  deleteEventPlayerResult,
  deleteEventTopCutResult,
  deleteCommanderPodPlayer,
  deleteCommanderPod,
  deleteCommanderRound,
  updatePlayerConstruct,
} from "../graphql/mutations";
import {
  listEventPlayerResults,
  listEventTopCutResults,
  listCommanderPodPlayers,
  listCommanderPods,
  listCommanderRounds,
  listPlayerConstructs,
} from "../graphql/queries";

const client = generateClient();

interface DeleteEventButtonProps {
  eventId: string;
  eventName: string;
  onDeleted?: () => void;
}

const DeleteEventButton: React.FC<DeleteEventButtonProps> = ({
  eventId,
  eventName,
  onDeleted,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const queryClient = useQueryClient();

  const handleDeleteEvent = async () => {
    // Show confirmation dialog
    const confirmMessage = `Are you sure you want to delete the event "${eventName}"?\n\nThis action will:\n• Delete all player results from this event\n• Delete all top cut results from this event\n• Update player statistics to remove this event's data\n• Permanently delete the event\n\nThis action cannot be undone!`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleting(true);
    try {
      console.log(`Starting deletion of event: ${eventId}`);

      // Step 1: Get all event player results
      console.log("Fetching event player results...");
      const playerResultsResult = await client.graphql({
        query: listEventPlayerResults,
        variables: {
          filter: { eventId: { eq: eventId } },
          limit: 1000,
        },
      });

      const playerResults =
        playerResultsResult.data.listEventPlayerResults.items;
      console.log(`Found ${playerResults.length} player results`);

      // Step 2: Get all event top cut results
      console.log("Fetching event top cut results...");
      const topCutResultsResult = await client.graphql({
        query: listEventTopCutResults,
        variables: {
          filter: { eventId: { eq: eventId } },
          limit: 1000,
        },
      });

      const topCutResults =
        topCutResultsResult.data.listEventTopCutResults.items;
      console.log(`Found ${topCutResults.length} top cut results`);

      const loadCommanderRecords = async (query: string, field: string) => {
        const response: any = await client.graphql({
          query,
          variables: {
            filter: { eventId: { eq: eventId } },
            limit: 1000,
          },
        });
        return response.data[field].items;
      };
      const [commanderPlayers, commanderPods, commanderRounds] =
        await Promise.all([
          loadCommanderRecords(
            listCommanderPodPlayers,
            "listCommanderPodPlayers"
          ),
          loadCommanderRecords(listCommanderPods, "listCommanderPods"),
          loadCommanderRecords(listCommanderRounds, "listCommanderRounds"),
        ]);

      // Step 3: Get all player constructs to update their stats
      console.log("Fetching player constructs...");
      const constructsResult = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });

      const allConstructs = constructsResult.data.listPlayerConstructs.items;
      console.log(`Found ${allConstructs.length} player constructs`);

      // Step 4: Delete all event player results
      console.log("Deleting event player results...");
      for (const result of playerResults) {
        try {
          await client.graphql({
            query: deleteEventPlayerResult,
            variables: { input: { id: result.id } },
          });
          console.log(`Deleted player result: ${result.id}`);
        } catch (error) {
          console.error(`Error deleting player result ${result.id}:`, error);
        }
      }

      // Step 5: Delete all event top cut results
      console.log("Deleting event top cut results...");
      for (const result of topCutResults) {
        try {
          await client.graphql({
            query: deleteEventTopCutResult,
            variables: { input: { id: result.id } },
          });
          console.log(`Deleted top cut result: ${result.id}`);
        } catch (error) {
          console.error(`Error deleting top cut result ${result.id}:`, error);
        }
      }

      for (const player of commanderPlayers) {
        await client.graphql({
          query: deleteCommanderPodPlayer,
          variables: { input: { id: player.id } },
        });
      }
      for (const pod of commanderPods) {
        await client.graphql({
          query: deleteCommanderPod,
          variables: { input: { id: pod.id } },
        });
      }
      for (const round of commanderRounds) {
        await client.graphql({
          query: deleteCommanderRound,
          variables: { input: { id: round.id } },
        });
      }

      // Step 6: Update player constructs to remove this event's stats
      console.log("Updating player constructs...");
      for (const result of playerResults) {
        const construct = allConstructs.find(
          (c) => c.id === result.playerConstructId
        );
        if (construct) {
          try {
            const updatedTotalEvents = Math.max(0, construct.totalEvents - 1);
            const updatedTotalPoints = Math.max(
              0,
              construct.totalPoints - result.points
            );
            const updatedTotalWins = Math.max(
              0,
              construct.totalWins - result.wins
            );
            const updatedTotalLosses = Math.max(
              0,
              construct.totalLosses - result.losses
            );
            const updatedTotalDraws = Math.max(
              0,
              construct.totalDraws - result.draws
            );

            await client.graphql({
              query: updatePlayerConstruct,
              variables: {
                input: {
                  id: construct.id,
                  totalEvents: updatedTotalEvents,
                  totalPoints: updatedTotalPoints,
                  totalWins: updatedTotalWins,
                  totalLosses: updatedTotalLosses,
                  totalDraws: updatedTotalDraws,
                  // Reset best rank and average rank since we're removing data
                  bestRank: null,
                  averageRank: null,
                },
              },
            });
            console.log(`Updated player construct: ${construct.name}`);
          } catch (error) {
            console.error(
              `Error updating player construct ${construct.id}:`,
              error
            );
          }
        }
      }

      // Step 7: Delete the event itself
      console.log("Deleting event...");
      await client.graphql({
        query: deleteEvent,
        variables: { input: { id: eventId } },
      });
      console.log(`Deleted event: ${eventId}`);

      await queryClient.invalidateQueries();
      console.log("✅ Event deletion completed successfully!");
      alert(`Event "${eventName}" has been deleted successfully!`);

      // Call the callback if provided
      if (onDeleted) {
        onDeleted();
      }
    } catch (error) {
      console.error("❌ Error during event deletion:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      alert(`Error deleting event: ${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Button
      variant="destructive"
      size="sm"
      disabled={isDeleting}
      onClick={handleDeleteEvent}
      className="flex items-center gap-2"
    >
      <Trash2 className="h-4 w-4" />
      {isDeleting ? "Deleting..." : "Delete Event"}
    </Button>
  );
};

export default DeleteEventButton;
